import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listSavedResponsesByBook,
  listSavedResponsesByList,
  listSavedResponsesLibraryWide,
  listAllSavedResponses,
  createSavedResponse,
  updateSavedResponseScope,
  deleteSavedResponse,
  type SavedChatResponse,
  type CreateSavedResponseInput,
  type UpdateSavedResponseScopeInput,
} from '../api/savedResponses';

export const savedResponseKeys = {
  all: ['savedResponses'] as const,
  byBook: (bookId: number) => [...savedResponseKeys.all, 'book', bookId] as const,
  byList: (listId: number) => [...savedResponseKeys.all, 'list', listId] as const,
  libraryWide: () => [...savedResponseKeys.all, 'library'] as const,
  allFlat: () => [...savedResponseKeys.all, 'all'] as const,
};

export function useSavedResponsesByBook(bookId: number) {
  return useQuery<SavedChatResponse[]>({
    queryKey: savedResponseKeys.byBook(bookId),
    queryFn: () => listSavedResponsesByBook(bookId),
    enabled: bookId > 0,
  });
}

export function useSavedResponsesByList(listId: number) {
  return useQuery<SavedChatResponse[]>({
    queryKey: savedResponseKeys.byList(listId),
    queryFn: () => listSavedResponsesByList(listId),
    enabled: listId > 0,
  });
}

export function useSavedResponsesLibraryWide() {
  return useQuery<SavedChatResponse[]>({
    queryKey: savedResponseKeys.libraryWide(),
    queryFn: () => listSavedResponsesLibraryWide(),
  });
}

export function useAllSavedResponses() {
  return useQuery<SavedChatResponse[]>({
    queryKey: savedResponseKeys.allFlat(),
    queryFn: () => listAllSavedResponses(),
  });
}

export function useCreateSavedResponse() {
  const qc = useQueryClient();
  return useMutation<SavedChatResponse, Error, CreateSavedResponseInput>({
    mutationFn: (input) => createSavedResponse(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: savedResponseKeys.all });
    },
  });
}

export function useUpdateSavedResponseScope() {
  const qc = useQueryClient();
  return useMutation<SavedChatResponse, Error, { id: number; input: UpdateSavedResponseScopeInput }>({
    mutationFn: ({ id, input }) => updateSavedResponseScope(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: savedResponseKeys.all });
    },
  });
}

export function useDeleteSavedResponse() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (id) => deleteSavedResponse(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: savedResponseKeys.all });
    },
  });
}
